import React, { useEffect, useState, useContext } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { LoadingButton, Dropdown, MenuItem, FormController, FormLabel, } from '@one-thd/sui-atomic-components';
import { AddressesCount } from '../../shared/addresses-count/addresses-count';
import { ERROR_CODES } from '../../../constants/custom-errors';
import { FORM_MODE } from '../../../models/common/addresses';
import { Alert } from '../../shared/alert/alert';
import { ERR_UNABLE_TO_SAVE, ERR_GENERIC, ERR_NO_SAVED_ADR, } from '../../../constants/response-error-messages';
import { AddressForm } from '../address-form/address-form';
import { AddressCard } from '../address-card/address-card';
import { useAddresses } from '../../../hooks/use-addresses';
import { Spinner } from '../../shared/spinner/spinner';
import { ADDED_ADDRESS_MSG, DELETED_ADDRESS_MSG, EDITED_ADDRESS_MSG, } from '../../../constants/response-success-messages';
import { B2C_ADDRESSES_LIMIT_REACHED_MSG, B2B_ADDRESSES_LIMIT_REACHED_MSG, } from '../../../constants/response-warning-messages';
import { ADDRESSES_LIMIT_B2C, ADDRESSES_LIMIT_B2B, } from '../../../constants/general';
import { addressSortInputValues, addressDefaultSortBy, } from '../../../constants/sort';
import { generateSortByOptions } from '../../../utils/sort';
import { Pagination } from '../../shared/pagination/pagination';
import { pushEventInfo, setPageInfo, pushErrorEvent, } from '../../../utils/analytics';
import { setDeleteAddressAnalytics, setFirstPageAnalytics, setPaginatedPageAnalytics, setSortAnalytics, } from '../../../utils/address-analytics';
export const AddressesContent = () => {
    var _a, _b;
    const { isB2B, addresses, addressFormMode, getAddresses, setAddressFormMode, setEditableAddress, } = useAddresses();
    const totalPages = Number((_a = addresses === null || addresses === void 0 ? void 0 : addresses.paginationInfo) === null || _a === void 0 ? void 0 : _a.totalPages) || 0;
    const { channel } = useContext(ExperienceContext);
    const isMobile = channel === 'mobile';
    const [isLoading, setIsLoading] = useState(false);
    const [deletedAddressIndex, setDeletedAddressIndex] = useState(null);
    const [successAddAddressMsg, setSuccessAddAddressMsg] = useState(null);
    const [editedAddressId, setEditedAddressId] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [generalError, setGeneralError] = useState(null);
    const [currentSortByValue, setCurrentSortByValue] = useState(addressDefaultSortBy);
    const [currentPageNumber, setCurrentPageNumber] = useState(1);
    const [newDefaultSuccessMessage, setNewDefaultSuccessMessage] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const { NO_SAVED_ADR_CODE } = ERROR_CODES;
    const addressCount = Number((_b = addresses === null || addresses === void 0 ? void 0 : addresses.paginationInfo) === null || _b === void 0 ? void 0 : _b.totalNumberOfRecords);
    const isB2CAddressLimitReached = !isB2B && addressCount === ADDRESSES_LIMIT_B2C;
    const isB2BAddressLimitReached = isB2B && addressCount === ADDRESSES_LIMIT_B2B;
    const addressLimitReached = isB2CAddressLimitReached || isB2BAddressLimitReached;
    const limitReachedMessage = isB2B
        ? B2B_ADDRESSES_LIMIT_REACHED_MSG.message
        : B2C_ADDRESSES_LIMIT_REACHED_MSG.message;
    const resetActionsState = () => {
        setDeletedAddressIndex(null);
        setErrorMessage(null);
        setSuccessAddAddressMsg(null);
        setEditedAddressId(null);
        setNewDefaultSuccessMessage(null);
    };
    const handleDeleteCardSuccess = (index) => {
        var _a;
        resetActionsState();
        setDeletedAddressIndex(index);
        const isLastPage = currentPageNumber > 1 && currentPageNumber === totalPages;
        const isLastAddressOnPage = ((_a = addresses === null || addresses === void 0 ? void 0 : addresses.paginationInfo) === null || _a === void 0 ? void 0 : _a.addressesOnCurrentPage) === 1;
        const newPage = isLastAddressOnPage && isLastPage
            ? currentPageNumber - 1
            : currentPageNumber;
        // ensure user stays on the same page or on the previous page (if last address deleted) to show delete confirmation
        triggerGetAddresses(currentSortByValue, newPage);
        setDeleteAddressAnalytics();
    };
    const handleDeleteCardFailure = () => {
        resetActionsState();
        setErrorMessage(ERR_UNABLE_TO_SAVE);
        pushErrorEvent('address card: delete', 'inline', 'n/a', ERR_UNABLE_TO_SAVE.message);
    };
    const handleDefaultChangeSuccess = () => {
        resetActionsState();
        triggerGetAddresses(currentSortByValue, 1);
        setNewDefaultSuccessMessage(EDITED_ADDRESS_MSG);
    };
    const handleDefaultChangeFailure = () => {
        resetActionsState();
        setErrorMessage(ERR_UNABLE_TO_SAVE);
        pushErrorEvent('address card: set as default', 'inline', 'n/a', ERR_UNABLE_TO_SAVE.message);
    };
    const onEditSuccess = (adrId) => {
        setEditedAddressId(adrId);
        setAddressFormMode(null);
        setEditableAddress(null);
        // reset addresses to last edited and page 1
        triggerGetAddresses('Last Edited', 1);
    };
    const onCloseEditedSuccessMsg = () => {
        setEditedAddressId(null);
        setNewDefaultSuccessMessage(null);
    };
    const onAddSuccess = () => {
        setAddressFormMode(null);
        setSuccessAddAddressMsg(ADDED_ADDRESS_MSG);
        setDeletedAddressIndex(null);
        // reset addresses to last edited and page 1
        triggerGetAddresses('Last Edited', 1);
    };
    const triggerGetAddresses = (sortBy, pageNumber = 1) => {
        const isAddressesPageChanged = currentPageNumber !== pageNumber;
        const options = generateSortByOptions(sortBy, { page: pageNumber });
        setErrorCode(null);
        setCurrentSortByValue(sortBy);
        if (isAddressesPageChanged) {
            setCurrentPageNumber(pageNumber);
            if (pageNumber !== 1) {
                setPaginatedPageAnalytics();
            }
            else {
                setFirstPageAnalytics();
            }
        }
        setIsLoading(true);
        return getAddresses(options)
            .catch((error) => {
            if (error.message === NO_SAVED_ADR_CODE) {
                setErrorCode(NO_SAVED_ADR_CODE);
                return;
            }
            setGeneralError(ERR_GENERIC);
            setPageInfo('error', 'page', 'addresses page', ERR_GENERIC.message, 'n/a');
        })
            .finally(() => {
            setIsLoading(false);
        });
    };
    useEffect(() => {
        triggerGetAddresses(currentSortByValue, currentPageNumber);
        setFirstPageAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const showAddAddressForm = () => {
        resetActionsState();
        const event = {
            category: {
                primaryCategory: 'overlay',
            },
            eventInfo: {
                eventName: 'add address',
            },
        };
        pushEventInfo(event);
        setAddressFormMode(FORM_MODE.ADD);
    };
    const DeletedAddressSuccessAlert = (React.createElement(Alert, { status: "success", key: "deletedAddress", onClose: () => {
            setDeletedAddressIndex(null);
        }, "data-testid": "success-address-deleted-msg" }, DELETED_ADDRESS_MSG.message));
    const EditedAddressSuccessAlert = (React.createElement("div", { className: "sui-mb-8" },
        React.createElement(Alert, { status: "success", onClose: onCloseEditedSuccessMsg, "data-testid": "success-edited-address-msg" }, EDITED_ADDRESS_MSG.message)));
    const onChangePage = (newPageNumber) => {
        resetActionsState();
        triggerGetAddresses(currentSortByValue, newPageNumber);
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
        });
    };
    const NoSavedAddressesMsg = (React.createElement(Alert, { status: "info" }, ERR_NO_SAVED_ADR.message));
    const executeSort = (_event, sortType) => {
        setSortAnalytics(sortType);
        triggerGetAddresses(sortType, 1);
    };
    const getAddressesList = () => {
        const addressList = ((addresses === null || addresses === void 0 ? void 0 : addresses.addresses) &&
            addresses.addresses.map((address, index) => (React.createElement("div", { key: address.addrIdentifier + '-' + address.lastModifiedDate, className: `sui-flex ${isMobile ? 'sui-flex-col' : ''}` },
                React.createElement(AddressCard, { index: index, address: address, onDeleteSuccess: handleDeleteCardSuccess, onDeleteFailure: handleDeleteCardFailure, onCloseSuccessMsg: onCloseEditedSuccessMsg, resetActionsState: resetActionsState, isShowEditedSuccessMsgMW: editedAddressId === address.addrIdentifier ||
                        (!!newDefaultSuccessMessage && address.isDefault), onDefaultChangeSuccess: handleDefaultChangeSuccess, onDefaultChangeFailure: handleDefaultChangeFailure }))))) ||
            [];
        if (isMobile && deletedAddressIndex !== null)
            addressList.splice(deletedAddressIndex, 0, DeletedAddressSuccessAlert);
        return addressList;
    };
    return !generalError ? (React.createElement(React.Fragment, null,
        React.createElement(Spinner, { active: isLoading }),
        errorMessage && !isMobile && (React.createElement(Alert, { status: "error" }, errorMessage.message)),
        deletedAddressIndex !== null && !isMobile && DeletedAddressSuccessAlert,
        (editedAddressId || newDefaultSuccessMessage) &&
            !isMobile &&
            EditedAddressSuccessAlert,
        successAddAddressMsg && (React.createElement("div", { className: "sui-mb-8" },
            React.createElement(Alert, { status: "success", onClose: () => setSuccessAddAddressMsg(null), "test-dataid": "success-add-address-msg" }, successAddAddressMsg.message))),
        addressFormMode ? (React.createElement(AddressForm, { onAddSuccess: onAddSuccess, onEditSuccess: onEditSuccess })) : (React.createElement(React.Fragment, null,
            addressLimitReached && (React.createElement(Alert, { status: "warning" }, limitReachedMessage)),
            React.createElement("div", { className: "sui-grid sui-mb-2 sui-gap-y-6 sui-gap-x-8 sui-grid-cols-1 md:sui-grid-cols-2 2xl:sui-grid-cols-3" },
                React.createElement("div", { className: "sui-self-end sui-col-span-1 sui-col-start-1" },
                    React.createElement(LoadingButton, { onClick: showAddAddressForm, fullWidth: true, disabled: addressLimitReached, "data-testid": "add-address-form-btn" }, "Add Address")),
                isB2B && (addresses === null || addresses === void 0 ? void 0 : addresses.addresses.length) && (React.createElement("div", { key: "sortByForm", className: "sui-col-span-1 md:sui-col-start-2 2xl:sui-col-start-3" },
                    React.createElement(FormController, { fullWidth: true },
                        React.createElement(FormLabel, { htmlFor: "sort-dropdown" }, "Sort By"),
                        React.createElement(Dropdown, { maxItems: 5, id: "sort-dropdown", "data-testid": "sort-dropdown", value: currentSortByValue, onChange: executeSort }, addressSortInputValues.map((sortBy) => (React.createElement(MenuItem, { key: sortBy, value: sortBy }, sortBy)))))))),
            !isLoading && errorCode === NO_SAVED_ADR_CODE && NoSavedAddressesMsg,
            React.createElement("div", { className: "sui-mt-6" },
                React.createElement(AddressesCount, { count: addressCount })),
            React.createElement("div", { className: "sui-grid sui-grid-cols-1 sui-gap-y-4 sui-mt-2 sm:sui-mt-1 sui-gap-x-8 sm:sui-gap-y-8 md:sui-grid-cols-2 2xl:sui-gap-y-16 2xl:sui-grid-cols-3", "data-testid": "addresses" }, getAddressesList()),
            totalPages > 1 && (React.createElement("div", { className: "sui-w-full sui-justify-center sui-p-5 sui-flex" },
                React.createElement(Pagination, { nPages: totalPages, onChangePage: onChangePage, page: currentPageNumber }))))))) : (React.createElement(Alert, { status: "error" }, generalError.message));
};
